import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useHistory, Link } from 'react-router-dom';
import { RecaptchaVerifier, signInWithPhoneNumber } from 'firebase/auth';

const LoginPage = () => {
  const [otp, setOtp] = useState('');
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { auth, verifyOtp, authLoading } = useAuth();
  const history = useHistory();
  const [phoneNumber, setPhoneNumber] = useState('');
  const [countryCode, setCountryCode] = useState('+62');
  const [fullPhoneNumber, setFullPhoneNumber] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);

  const countryCodes = [
    { value: '+62', label: 'ID (+62)' },
    { value: '+65', label: 'SG (+65)' },
    { value: '+60', label: 'MY (+60)' },
    { value: '+1', label: 'US (+1)' },
  ];

  // Inisialisasi reCAPTCHA saat komponen dimuat
  useEffect(() => {
    if (!window.recaptchaVerifier) {
      window.recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
        'size': 'invisible',
      });
    }

    // Fungsi cleanup untuk membersihkan verifier saat komponen unmount
    return () => {
      if (window.recaptchaVerifier) {
        window.recaptchaVerifier.clear();
        window.recaptchaVerifier = null;
      }
    };
  }, [auth]);

  // Timer untuk cooldown pengiriman ulang OTP
  useEffect(() => {
    let timer;
    if (resendCooldown > 0) {
      timer = setInterval(() => {
        setResendCooldown(prev => prev - 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [resendCooldown]);

  const handleSendOtp = async (e) => {
    if (e) e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const fullNumber = countryCode + phoneNumber;
      setFullPhoneNumber(fullNumber);
      const result = await signInWithPhoneNumber(auth, fullNumber, window.recaptchaVerifier);
      setConfirmationResult(result);
      setResendCooldown(30); // Atur cooldown 30 detik
    } catch (err) {
      if (err.code === 'auth/timeout' || err.code === 'auth/network-request-failed') {
        // Jangan tampilkan error timeout ke pengguna, dan jangan log sebagai error
        // console.debug('Timeout/Network error suppressed:', err);
      } else {
        setError('Gagal mengirim OTP. Silakan periksa nomor telepon dan coba lagi.');
        console.error(err);
      }
      if (window.recaptchaVerifier) {
        window.recaptchaVerifier.clear();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await verifyOtp(confirmationResult, otp);
      history.push('/dashboard'); // Pengalihan setelah verifikasi berhasil
    } catch (err) {
      setError('Gagal memverifikasi OTP. Silakan periksa kode dan coba lagi.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-brand-dark font-custom p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-block">
            <h1 className="text-3xl sm:text-4xl font-extrabold text-brand-lime tracking-tighter">5.AM</h1>
            <span className="text-xl sm:text-2xl font-semibold text-white ml-2 tracking-wide">Padel Club</span>
          </Link>
        </div>

        <div className="bg-brand-gray p-6 sm:p-8 rounded-xl shadow-2xl border border-brand-terracotta/20">
          {!confirmationResult ? (
            <form onSubmit={handleSendOtp}>
              <h2 className="text-2xl font-bold text-white text-center mb-2">Selamat Datang Kembali</h2>
              <p className="text-center text-gray-400 mb-6">Masukkan nomor telepon Anda untuk masuk.</p>

              {error && <p className="bg-red-500/20 text-red-400 p-3 rounded-lg mb-4 text-sm text-center">{error}</p>}

              <div className="mb-6">
                <label htmlFor="phone" className="block text-gray-400 text-sm font-bold mb-2">
                  Nomor Telepon
                </label>
                <div className="flex">
                  <select
                    value={countryCode}
                    onChange={(e) => setCountryCode(e.target.value)}
                    className="bg-brand-dark border-2 border-brand-terracotta/30 rounded-l-lg py-3 px-2 text-white focus:outline-none focus:border-brand-lime transition duration-300 appearance-none"
                  >
                    {countryCodes.map(country => (
                      <option key={country.value} value={country.value} className="bg-brand-dark">{country.label}</option>
                    ))}
                  </select>
                  <input
                    type="tel"
                    id="phone"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    className="w-full bg-brand-dark border-2 border-y-brand-terracotta/30 border-r-brand-terracotta/30 border-l-0 rounded-r-lg py-3 px-4 text-white leading-tight focus:outline-none focus:border-brand-lime transition duration-300"
                    placeholder="81234567890"
                    required
                  />
                </div>
              </div>

              <div id="recaptcha-container" className="flex justify-center my-4"></div>

              <button
                type="submit"
                disabled={loading || authLoading}
                className="w-full bg-brand-lime text-brand-dark font-bold py-3 px-4 rounded-full text-lg hover:bg-opacity-90 transition duration-300 transform hover:scale-105 disabled:opacity-50 disabled:scale-100"
              >
                {authLoading ? 'Memuat layanan...' : (loading ? 'Mengirim OTP...' : 'Kirim OTP')}
              </button>
            </form>
          ) : (
            <form onSubmit={handleVerifyOtp}>
              <h2 className="text-2xl font-bold text-white text-center mb-2">Verifikasi Kode</h2>
              <p className="text-center text-gray-400 mb-6">Kode OTP telah dikirim ke {fullPhoneNumber}.</p>

              {error && <p className="bg-red-500/20 text-red-400 p-3 rounded-lg mb-4 text-sm text-center">{error}</p>}

              <div className="mb-6">
                <label htmlFor="otp" className="block text-gray-400 text-sm font-bold mb-2">
                  Kode OTP
                </label>
                <input
                  type="text"
                  id="otp"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  className="w-full bg-brand-dark border-2 border-brand-terracotta/30 rounded-lg py-3 px-4 text-white leading-tight focus:outline-none focus:border-brand-lime transition duration-300"
                  placeholder="123456"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-brand-lime text-brand-dark font-bold py-3 px-4 rounded-full text-lg hover:bg-opacity-90 transition duration-300 transform hover:scale-105 disabled:opacity-50 disabled:scale-100"
              >
                {loading ? 'Memverifikasi...' : 'Masuk'}
              </button>
              <div className="text-center mt-4">
                <button
                  type="button"
                  onClick={() => handleSendOtp()} // Memanggil handleSendOtp tanpa event
                  disabled={resendCooldown > 0}
                  className="text-brand-terracotta text-sm hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {resendCooldown > 0 ? `Kirim ulang dalam ${resendCooldown} detik` : 'Kirim ulang OTP'}
                </button>
              </div>
              <button
                type="button"
                onClick={() => setConfirmationResult(null)}
                className="w-full text-center text-brand-terracotta mt-4 text-sm hover:underline"
              >
                Gunakan nomor telepon lain
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginPage;